import { QueryClient } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import { Outlet } from 'react-router'
import { getVcMatchingLoader } from '@/feature/fundraising-advisory/vc-matching/api/loader.ts'
import { SkeletonPageLoading } from '@/components/shared/loading/page.tsx'
import { VcMatchesPageSkeleton } from '@/feature/fundraising-advisory/vc-matching/components/VcMatchesPageSkeleton.tsx'
import { getCompetitorsFundingAnalysisLoader } from '@/feature/fundraising-advisory/competitors-funding-analysis/api/loader.ts'
import { CfaPageSkeleton } from '@/feature/fundraising-advisory/competitors-funding-analysis/components/CfaPageSkeleton.tsx'
import { BusinessInfoPageSkeleton } from '@/feature/fundraising-advisory/business-information/components/BusinessInfoPageSkeleton.tsx'
import { getBusinessInformationLoader } from '@/feature/fundraising-advisory/business-information/api/loader.ts'
import { FundingHistoryPageSkeleton } from '@/feature/fundraising-advisory/funding-history/components/FundingHistoryPageSkeleton.tsx'
import { FundraisingAdvisoryRoute } from './route.tsx'
import { importOrReload } from '@/lib/utils/dynamic-import.ts'

const LazyCompetitorFundingAnalysis = lazy(() =>
  importOrReload(() =>
    import(`./competitor-funding-analysis/route.tsx`).then(mod => ({
      default: mod.CompetitorFundingAnalysisRoute
    }))
  )
)

const LazyVcMatching = lazy(() =>
  importOrReload(() =>
    import(`./vc-matching/route.tsx`).then(mod => ({
      default: mod.VcMatching
    }))
  )
)

const LazyBusinessInformationRoute = lazy(() =>
  importOrReload(() =>
    import(`./business-information/route.tsx`).then(mod => ({
      default: mod.BusinessInformationRoute
    }))
  )
)

const LazyFundingHistoryRoute = lazy(() =>
  importOrReload(() =>
    import(`./funding-history/route.tsx`).then(mod => ({
      default: mod.FundingHistoryRoute
    }))
  )
)

type RouterProps = {
  queryClient: QueryClient
}
export const useFundraisingAdvisoryRoutes = ({ queryClient }: RouterProps) => [
  {
    path: `fundraising`,
    element: <Outlet />,
    hydrateFallbackElement: <SkeletonPageLoading />,
    children: [
      { index: true, element: <FundraisingAdvisoryRoute /> },
      {
        path: `competitors-funding-analysis`,
        loader: getCompetitorsFundingAnalysisLoader(queryClient),
        element: (
          <Suspense fallback={<CfaPageSkeleton />}>
            <LazyCompetitorFundingAnalysis />
          </Suspense>
        )
      },
      {
        path: `vc-matching`,
        loader: getVcMatchingLoader(queryClient),
        element: (
          <Suspense fallback={<VcMatchesPageSkeleton />}>
            <LazyVcMatching />
          </Suspense>
        )
      },
      {
        path: `business-information`,
        loader: getBusinessInformationLoader(queryClient),
        element: (
          <Suspense fallback={<BusinessInfoPageSkeleton />}>
            <LazyBusinessInformationRoute />
          </Suspense>
        )
      },
      {
        path: `funding-history`,
        element: (
          <Suspense fallback={<FundingHistoryPageSkeleton />}>
            <LazyFundingHistoryRoute />
          </Suspense>
        )
      }
    ]
  }
]
